import CheckCircleIcon from '@icons/check-circle'
import ArrowTopRightOnSquareIcon from '@icons/arrow-top-right-on-square'
import { STATUS_LINKS } from '@/lib/definitions/constants'
import StatusEmailSubscriptionBlock from './StatusEmailSubscriptionBlock'
import StatusFeedEntryCard from './StatusFeedEntryCard'
import StatusReportTimelineCard from './StatusReportTimelineCard'
import StatusSupportPanel from './StatusSupportPanel'
import { getStatusLevelStyles } from './utils'
import type {
  CombinedStatusResponse,
  StatusFeedEntry,
  StatusLevel,
  StatusReportWithTimeline,
} from './types'

interface Props {
  isPopoverOpen: boolean
  statusEmailSubscription: {
    visible: boolean
    userEmail: string
  }
  withSupport: boolean
  showAllGoodPopoverLayout: boolean
  latestTimestamp: string | null
  level: StatusLevel
  isLoading: boolean
  hasError: boolean
  combined: CombinedStatusResponse | null
  reportEntries: StatusReportWithTimeline[]
  maintenanceEntries: StatusFeedEntry[]
  hasActiveFeed: boolean
  isAllQuiet: boolean
}

interface StatusPopoverEmailFooterProps {
  isPopoverOpen: boolean
  statusEmailSubscription: Props['statusEmailSubscription']
}

function StatusPopoverEmailFooter({
  isPopoverOpen,
  statusEmailSubscription,
}: StatusPopoverEmailFooterProps) {
  return (
    <>
      {statusEmailSubscription.visible && (
        <StatusEmailSubscriptionBlock
          isPopoverOpen={isPopoverOpen}
          userEmail={statusEmailSubscription.userEmail}
        />
      )}
      <div className="border-t border-neutral-10 divide-y divide-neutral-10">
        <div className="flex items-center justify-between px-5 py-3">
          <a
            href={STATUS_LINKS.STATUS_EVENTS}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-neutral-40 transition hover:text-neutral-60"
          >
            View status history →
          </a>
          <a
            href={STATUS_LINKS.STATUS_PAGE}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-neutral-50 transition hover:text-neutral-60 flex items-center gap-1"
          >
            Status page <ArrowTopRightOnSquareIcon className="size-4" />
          </a>
        </div>
      </div>
    </>
  )
}

const StatusPopoverBody = ({
  isPopoverOpen,
  statusEmailSubscription,
  withSupport,
  showAllGoodPopoverLayout,
  latestTimestamp,
  level,
  isLoading,
  hasError,
  combined,
  reportEntries,
  maintenanceEntries,
  hasActiveFeed,
}: Props) => {
  const levelStyles = getStatusLevelStyles(level)

  return (
    <div className={withSupport ? 'flex flex-col md:flex-row' : ''}>
      {withSupport && <StatusSupportPanel />}

      <div className="flex min-w-0 flex-1 flex-col bg-white">
        {showAllGoodPopoverLayout ? (
          <div className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 text-center">
              <div
                className="relative flex items-center justify-center"
                aria-hidden
              >
                <div className="absolute h-24 w-24 rounded-full bg-emerald-400/20 blur-2xl" />
                <CheckCircleIcon className="relative h-14 w-14 text-emerald-500" />
              </div>

              <p className="mt-4 text-base font-semibold text-neutral-60">
                All Systems Operational
              </p>

              <p className="mt-1.5 max-w-[20rem] text-sm leading-relaxed text-neutral-40">
                No incidents or maintenance in progress. Everything is running
                as expected.
              </p>

              {latestTimestamp && (
                <p className="mt-3 text-xs tabular-nums text-neutral-30">
                  Updated{' '}
                  {new Date(latestTimestamp).toLocaleString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              )}
            </div>

            <StatusPopoverEmailFooter
              isPopoverOpen={isPopoverOpen}
              statusEmailSubscription={statusEmailSubscription}
            />
          </div>
        ) : (
          <>
            <div className="border-b border-neutral-20/60 px-4 pb-3.5 pt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${levelStyles.dotClass}`}
                      aria-hidden
                    />
                    <p className="text-sm font-semibold text-neutral-60">
                      Status
                    </p>
                    {isLoading && !hasError && (
                      <span className="inline-flex items-center rounded-full bg-neutral-10 px-2 py-0.5 text-sm font-medium text-neutral-50 ring-1 ring-inset ring-neutral-20">
                        Checking...
                      </span>
                    )}
                    {!isLoading && !hasError && (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium ring-1 ring-inset ${levelStyles.feedBadgeClass}`}
                      >
                        {levelStyles.label}
                      </span>
                    )}
                  </div>

                  {combined && !hasError && (
                    <p className="mt-2 leading-snug text-neutral-50">
                      {combined.has_ongoing_maintenance
                        ? 'Maintenance is currently in progress. Some services may be briefly unavailable.'
                        : combined.has_planned_maintenance
                          ? 'All systems running. A maintenance window is scheduled ahead.'
                          : reportEntries.length > 0
                            ? 'Active incidents are listed below from our public status feed.'
                            : "Everything is working as expected. You're all set."}
                    </p>
                  )}

                  {latestTimestamp && !hasError && (
                    <p className="mt-1.5 text-sm text-neutral-40">
                      Updated{' '}
                      {new Date(latestTimestamp).toLocaleString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  )}

                  {isLoading && !hasError && (
                    <p className="mt-2 text-sm text-neutral-40">
                      Fetching the latest incident and maintenance information.
                    </p>
                  )}

                  {hasError && (
                    <p className="mt-2 text-sm text-neutral-40">
                      Unable to load latest status.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div
              className={
                withSupport
                  ? 'max-h-80 overflow-y-auto px-3 py-3'
                  : 'max-h-[min(50vh,22rem)] overflow-y-auto px-3 py-3'
              }
            >
              {hasError && (
                <div className="px-2 py-4 text-sm text-red-500">
                  Could not load status. Try the status page link above.
                </div>
              )}

              {isLoading && !hasError && !hasActiveFeed && (
                <div className="px-2 py-4 text-sm text-neutral-40">
                  Loading status feed...
                </div>
              )}

              {!hasError && hasActiveFeed && (
                <div className="space-y-4">
                  {reportEntries.length > 0 && (
                    <div>
                      <p className="mb-2 px-1 text-sm font-semibold uppercase tracking-wider text-neutral-40">
                        Current reports
                      </p>
                      <div className="space-y-3">
                        {reportEntries.slice(0, 5).map((report) => (
                          <StatusReportTimelineCard
                            key={report.id}
                            report={report}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {maintenanceEntries.length > 0 && (
                    <div>
                      <p className="mb-2 px-1 text-sm font-semibold uppercase tracking-wider text-neutral-40">
                        Maintenance
                      </p>
                      <div className="space-y-0.5">
                        {maintenanceEntries.slice(0, 5).map((entry) => (
                          <StatusFeedEntryCard key={entry.id} entry={entry} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <StatusPopoverEmailFooter
              isPopoverOpen={isPopoverOpen}
              statusEmailSubscription={statusEmailSubscription}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default StatusPopoverBody
